import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ControlContainer,
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MatNativeDateModule,
} from '@angular/material/core';
import {
  MatDatepicker,
  MatDatepickerInputEvent,
  MatDatepickerModule,
} from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { fromEvent, startWith, Subscription, switchMap, take, takeUntil } from 'rxjs';
import {
  BASE_ELEMENT_DIRECTIVE_INPUTS,
  BaseElementDirective,
} from '../base-element-directive';
import { TooltipComponent } from '../tooltip/tooltip.component';
import {
  DATE_INPUT_OPTIONS,
  OMM_DATE_ONLY_DISPLAY,
  OMM_DATE_TIME_DISPLAY,
  OMM_DATE_TIME_INPUT_FORMATS,
  OMM_TIME_ONLY_DISPLAY,
} from './constants';
import { resolveUserLocale } from '@/services/locale/user-locale.util';
import { TranslocoPageRouteSyncService } from '@/core/transloco/transloco-loader';
import { TranslocoService } from '@jsverse/transloco';
import { DatepickerHeaderComponent } from './header/datepicker-header.component';
import { TimePickerPanelComponent } from './time-picker-panel/time-picker-panel.component';
import {
  TranslocoDateAdapter,
  USER_MAT_DATE_LOCALE_PROVIDER,
} from './transloco-date-adapter';
import { DateReturnType } from './types';

/**
 * DateInputComponent - Material date/time picker component
 *
 * Set [showTime]="true" for date-time mode (date + time).
 * Set [timeOnly]="true" to open the time picker directly (no calendar).
 * Uses custom calendar styling (blue header, selected date circle).
 *
 * With formControlName (reactive forms):
 * ```html
 * <app-date-input
 *   formControlName="birthDate"
 *   [returnType]="'number'"
 *   [label]="'Birth Date'"
 * />
 * ```
 *
 * With date-time (time picker opens after selecting a date):
 * ```html
 * <app-date-input
 *   formControlName="orderDate"
 *   [showTime]="true"
 *   [label]="'Customer Order Date'"
 * />
 * ```
 *
 * With time-only (time picker opens on click):
 * ```html
 * <app-date-input
 *   formControlName="startTime"
 *   [timeOnly]="true"
 *   [label]="'Start time'"
 * />
 * ```
 */
@Component({
  selector: 'app-date-input',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatTooltipModule,
    MatDatepickerModule,
    MatNativeDateModule,
    TooltipComponent,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateInputComponent),
      multi: true,
    },
    { provide: DateAdapter, useClass: TranslocoDateAdapter },
    USER_MAT_DATE_LOCALE_PROVIDER,
    {
      provide: DATE_INPUT_OPTIONS,
      useFactory: () => ({ showTime: false, timeOnly: false }),
    },
    {
      provide: MAT_DATE_FORMATS,
      useFactory: () => {
        const options = inject(DATE_INPUT_OPTIONS);
        return {
          ...OMM_DATE_TIME_INPUT_FORMATS,
          display: {
            ...OMM_DATE_TIME_INPUT_FORMATS.display,
            get dateInput() {
              if (options.timeOnly) {
                return OMM_TIME_ONLY_DISPLAY;
              }
              return options.showTime
                ? OMM_DATE_TIME_DISPLAY
                : OMM_DATE_ONLY_DISPLAY;
            },
          },
        };
      },
    },
  ],
  hostDirectives: [
    {
      directive: BaseElementDirective,
      inputs: BASE_ELEMENT_DIRECTIVE_INPUTS,
      outputs: ['dataChange'],
    },
  ],
  templateUrl: './date-input.component.html',
  styleUrl: './date-input.component.scss',
  viewProviders: [
    {
      provide: ControlContainer,
      useFactory: () =>
        inject(ControlContainer, { skipSelf: true, optional: true }),
    },
  ],
})
export class DateInputComponent
  implements ControlValueAccessor, OnInit, OnChanges
{
  baseElement = inject(BaseElementDirective<DateReturnType>);
  private _cdr = inject(ChangeDetectorRef);
  private _overlay = inject(Overlay);
  private _viewContainerRef = inject(ViewContainerRef);
  private _dateInputOptions = inject(DATE_INPUT_OPTIONS);
  private destroyRef = inject(DestroyRef);
  private readonly pageI18n = inject(TranslocoPageRouteSyncService);
  private readonly transloco = inject(TranslocoService);
  @Input() tooltip?: string;
  @Input() disableTooltip?: boolean = false;
  @Input() wrapperClasses?: string;
  @Input() labelClasses?: string;
  @Input() inputClasses?: string;
  @Input() placeholder?: string;
  @Input() minDate?: DateReturnType;
  @Input() maxDate?: DateReturnType;

  @Input() returnType: 'date' | 'number' | 'string' = 'date';

  // When true, after selecting a date the time picker opens
  @Input() showTime = false;

  /** When true, skips the calendar and opens the time picker directly. */
  @Input() timeOnly = false;

  /** Applied when picking a date in showTime mode (calendar sets midnight otherwise). */
  @Input() defaultTime: { hours: number; minutes: number } | null = null;

  @Output() valueChanges = new EventEmitter<DateReturnType>();

  dateControl = new FormControl<Date | null>(null);

  @ViewChild('dateTimeOrigin', { read: ElementRef })
  dateTimeOriginRef!: ElementRef<HTMLElement>;
  @ViewChild('dateInput', { read: ElementRef })
  dateInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild('picker') picker?: MatDatepicker<Date>;

  private _timePickerOverlayRef: OverlayRef | null = null;
  private _timePickerSubscriptions = new Subscription();
  // Stored when opening time picker so we can restore on cancel/backdrop
  private _timePickerOpenedWithDate: Date | null = null;
  /** Date waiting for the calendar overlay to finish closing before time picker opens. */
  private _pendingTimePickerDate: Date | null = null;
  /** True when a calendar day cell was clicked (including the already-selected day). */
  private _calendarDayPicked = false;
  private _calendarPickSubscription = Subscription.EMPTY;

  readonly customHeader = DatepickerHeaderComponent;

  readonly dateInputId = `omm-date-input-${Math.random().toString(36).slice(2)}`;

  private readonly _timeDisplayFormatter = new Intl.DateTimeFormat(
    resolveUserLocale(),
    OMM_TIME_ONLY_DISPLAY
  );

  /** When false, the calendar icon is hidden and the picker cannot be opened (readonly/disabled). */
  get isDatepickerEnabled(): boolean {
    return (
      this.baseElement.editMode &&
      !this.baseElement.isDisabled &&
      !this.baseElement.isReadonly
    );
  }

  get timeDisplayValue(): string {
    const value = this.dateControl.value;
    if (!value) {
      return '';
    }
    return this._timeDisplayFormatter.format(value);
  }

  private _value: DateReturnType = null;
  private _valueSet = false;

  ngOnInit(): void {
    this.transloco.langChanges$
      .pipe(
        startWith(this.transloco.getActiveLang()),
        takeUntilDestroyed(this.destroyRef),
        switchMap(() => this.pageI18n.ensureKnownComponentBundle('date-input'))
      )
      .subscribe();

    this._syncDateInputOptions();
    if (!this._valueSet && this.baseElement.data !== undefined) {
      this._value = this.baseElement.data as DateReturnType;
    }

    this.updateDateControl();
    this.updateDisabledState();
    // Re-set value so the input re-formats with date-only when showTime is false
    const value = this.dateControl.value;
    if (value != null) {
      this.dateControl.setValue(value, { emitEvent: false });
    }

    this.dateControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        this._value = value;
        const convertedValue = this.convertValue(value);

        this.onChange(convertedValue);
        this.onTouched();

        this.valueChanges.emit(convertedValue);
        this.baseElement.emitDataChange(convertedValue);
      });
  }

  /** Material calendar needs a Date; callers may pass epoch ms / ISO string. */
  get resolvedMinDate(): Date | null {
    return this.toDateBound(this.minDate);
  }

  get resolvedMaxDate(): Date | null {
    return this.toDateBound(this.maxDate);
  }

  // Called when user confirms time in the time picker panel. Merges time into dateControl and closes overlay
  onTimeSelected(date: Date): void {
    const base =
      this.dateControl.value ?? this._timePickerOpenedWithDate ?? date;
    const merged = new Date(base.getTime());
    merged.setHours(
      date.getHours(),
      date.getMinutes(),
      date.getSeconds(),
      date.getMilliseconds()
    );
    const clamped = this.clampToBounds(merged);
    this._value = clamped;
    this.dateControl.setValue(clamped);
    const convertedValue = this.convertValue(clamped);
    this.onChange(convertedValue);
    this.onTouched();
    this.valueChanges.emit(convertedValue);
    this.baseElement.emitDataChange(convertedValue);
    this._closeTimePickerOverlay();
  }

  private updateDisabledState(): void {
    const shouldDisable =
      this.baseElement.isDisabled ||
      !this.baseElement.editMode ||
      this.baseElement.isReadonly;
    if (shouldDisable && this.dateControl.enabled) {
      this.dateControl.disable({ emitEvent: false });
    } else if (!shouldDisable && this.dateControl.disabled) {
      this.dateControl.enable({ emitEvent: false });
    }
  }

  private updateDateControl(): void {
    const dateValue = this.currentValue;
    if (this.dateControl.value !== dateValue) {
      this.dateControl.setValue(dateValue, { emitEvent: false });
    }
  }

   
  private onChange = (_value: DateReturnType): void => {
    // Set by registerOnChange
  };

  private onTouched = (): void => {
    // Set by registerOnTouched
  };

  // ControlValueAccessor implementation
  writeValue(value: DateReturnType): void {
    this._value = value ?? null;
    this._valueSet = true;
    this.updateDateControl();
  }

  registerOnChange(fn: (value: DateReturnType) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.baseElement.disabledExternal = isDisabled;
    if (isDisabled) {
      this.dateControl.disable({ emitEvent: false });
    } else {
      this.dateControl.enable({ emitEvent: false });
    }
  }

  get currentValue(): Date | null {
    let value: DateReturnType = null;

    if (this._valueSet) {
      value = this._value;
    } else if (this.baseElement.data !== undefined) {
      value = this.baseElement.data as DateReturnType;
    } else {
      value = this._value;
    }

    if (value === null) {
      return null;
    }

    // If value is a number (epoch), convert to Date
    if (typeof value === 'number') {
      const date = new Date(value);
      return isNaN(date.getTime()) ? null : date;
    }

    // Convert string to Date if needed (support numeric timestamp strings)
    if (typeof value === 'string' && value) {
      const n = Number(value);
      if (Number.isFinite(n)) {
        const date = new Date(n);
        return isNaN(date.getTime()) ? null : date;
      }
      const date = new Date(value);
      return isNaN(date.getTime()) ? null : date;
    }

    // Return Date or null
    return value instanceof Date ? value : null;
  }

  onDateInput(event: MatDatepickerInputEvent<Date>): void {
    this.applyPickedDate(event.value);
  }

  onDateChange(event: MatDatepickerInputEvent<Date>): void {
    const value = this.applyPickedDate(event.value);
    if (this.showTime && value) {
      // Open after the calendar closes; opening now races the datepicker teardown.
      this._calendarDayPicked = true;
      this._pendingTimePickerDate = new Date(value.getTime());
      if (!this.picker?.opened) {
        this.flushPendingTimePicker();
      }
    }
  }

  onDatepickerClosed(): void {
    this._calendarPickSubscription.unsubscribe();
    this._calendarPickSubscription = Subscription.EMPTY;

    if (!this.showTime || !this._calendarDayPicked) {
      this._calendarDayPicked = false;
      this._pendingTimePickerDate = null;
      return;
    }

    this._calendarDayPicked = false;
    // Same-day reselect often skips dateChange; fall back to the current value.
    if (!this._pendingTimePickerDate && this.dateControl.value) {
      this._pendingTimePickerDate = new Date(this.dateControl.value.getTime());
    }
    this.flushPendingTimePicker();
  }

  onInputClick(): void {
    if (!this.isDatepickerEnabled) return;
    if (this.timeOnly) {
      this.openTimePicker();
      return;
    }
    this.picker?.open();
  }

  openTimePicker(): void {
    if (!this.isDatepickerEnabled) return;
    this._pendingTimePickerDate = null;
    this._calendarDayPicked = false;
    const initial = this.dateControl.value ?? this.buildDefaultTimeOnlyDate();
    this._cdr.detectChanges();
    this._openTimePicker(initial);
  }

  private applyPickedDate(raw: Date | null): Date | null {
    let value = raw ? new Date(raw.getTime()) : null;
    // Calendar picks arrive at midnight; apply defaultTime when present.
    if (
      this.showTime &&
      value &&
      this.defaultTime &&
      value.getHours() === 0 &&
      value.getMinutes() === 0 &&
      value.getSeconds() === 0 &&
      value.getMilliseconds() === 0
    ) {
      value.setHours(this.defaultTime.hours, this.defaultTime.minutes, 0, 0);
    }
    if (value) {
      value = this.clampToBounds(value);
    }
    if (this.dateControl.value?.getTime() !== value?.getTime()) {
      this.dateControl.setValue(value);
    }
    return value;
  }

  private toDateBound(value: DateReturnType | undefined): Date | null {
    if (value == null || value === '') {
      return null;
    }
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }
    if (typeof value === 'number') {
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? null : date;
    }
    if (typeof value === 'string') {
      const asNumber = Number(value);
      const date = Number.isFinite(asNumber)
        ? new Date(asNumber)
        : new Date(value);
      return Number.isNaN(date.getTime()) ? null : date;
    }
    return null;
  }

  private clampToBounds(date: Date): Date {
    let next = new Date(date.getTime());
    const min = this.resolvedMinDate;
    const max = this.resolvedMaxDate;
    if (min && next.getTime() < min.getTime()) {
      next = new Date(min.getTime());
    }
    if (max && next.getTime() > max.getTime()) {
      next = new Date(max.getTime());
    }
    return next;
  }

  private flushPendingTimePicker(): void {
    const pending = this._pendingTimePickerDate;
    if (!pending || !this.showTime) return;
    this._pendingTimePickerDate = null;
    // Let MatDatepicker finish disposing its overlay/backdrop first.
    setTimeout(() => {
      this._cdr.detectChanges();
      this._openTimePicker(pending);
    });
  }

  private buildDefaultTimeOnlyDate(): Date {
    const date = new Date();
    const hours = this.defaultTime?.hours ?? 0;
    const minutes = this.defaultTime?.minutes ?? 0;
    date.setHours(hours, minutes, 0, 0);
    return date;
  }

  private _syncDateInputOptions(): void {
    this._dateInputOptions.showTime = this.showTime || this.timeOnly;
    this._dateInputOptions.timeOnly = this.timeOnly;
  }

  onDatepickerOpened(): void {
    this._calendarDayPicked = false;
    this._pendingTimePickerDate = null;
    this._calendarPickSubscription.unsubscribe();

    // dateChange does not fire when re-selecting the current day; track cell clicks.
    if (this.showTime && this.picker) {
      this._calendarPickSubscription = fromEvent<MouseEvent>(
        document,
        'mousedown',
        { capture: true }
      )
        .pipe(takeUntil(this.picker.closedStream))
        .subscribe((event: MouseEvent) => {
          const target = event.target as HTMLElement | null;
          if (
            target?.closest(
              '.mat-calendar-body-cell:not(.mat-calendar-body-disabled)'
            )
          ) {
            this._calendarDayPicked = true;
          }
        });
    }

    // MatDatepicker shifts focus to calendar cells on open; restore input focus after that.
    [0, 40, 120, 240, 400].forEach(delayMs => {
      window.setTimeout(() => {
        const input = this.dateInputRef?.nativeElement;
        if (!input) return;
        input.focus();
        if (input.value?.length) {
          input.select();
        }
      }, delayMs);
    });
  }

  private _openTimePicker(initialDate: Date): void {
    this._closeTimePickerOverlay();
    const origin = this.dateTimeOriginRef?.nativeElement;
    if (!origin) return;
    this._timePickerOverlayRef = this._overlay.create({
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      scrollStrategy: this._overlay.scrollStrategies.block(),
      disposeOnNavigation: true,
      positionStrategy: this._overlay
        .position()
        .flexibleConnectedTo(origin)
        .withPositions([
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
          },
          {
            originX: 'start',
            originY: 'top',
            overlayX: 'start',
            overlayY: 'bottom',
          },
        ])
        .withPush(true),
    });
    // Preserve prior control value (including null) so Cancel can restore it.
    this._timePickerOpenedWithDate = this.dateControl.value
      ? new Date(this.dateControl.value.getTime())
      : null;
    this._timePickerOverlayRef
      .backdropClick()
      .pipe(take(1))
      .subscribe(() => {
        // Outside click keeps the current pick (same as OK), Cancel restores.
        const current = this.dateControl.value;
        if (current) {
          this.onTimeSelected(current);
        } else {
          this._closeTimePickerOverlay(false);
        }
      });
    const portal = new ComponentPortal(
      TimePickerPanelComponent,
      this._viewContainerRef
    );
    const ref = this._timePickerOverlayRef.attach(portal);
    const boundedInitial = this.clampToBounds(new Date(initialDate.getTime()));
    ref.instance.minDate = this.resolvedMinDate;
    ref.instance.maxDate = this.resolvedMaxDate;
    ref.instance.initialTime = boundedInitial;
    this._timePickerSubscriptions = new Subscription();
    this._timePickerSubscriptions.add(
      ref.instance.timeSelected
        .pipe(take(1))
        .subscribe((d: Date) => this.onTimeSelected(d))
    );
    this._timePickerSubscriptions.add(
      ref.instance.timeChanged.subscribe((d: Date) => {
        this.dateControl.setValue(this.clampToBounds(d), { emitEvent: false });
        this._cdr.markForCheck();
      })
    );
    this._timePickerSubscriptions.add(
      ref.instance.cancelled
        .pipe(take(1))
        .subscribe(() => this._closeTimePickerOverlay(true))
    );
  }

  private _closeTimePickerOverlay(restore = false): void {
    if (this._timePickerOverlayRef) {
      if (restore) {
        this.dateControl.setValue(this._timePickerOpenedWithDate, {
          emitEvent: false,
        });
        this._cdr.markForCheck();
      }
      this._timePickerOpenedWithDate = null;
      this._timePickerSubscriptions.unsubscribe();
      this._timePickerSubscriptions = new Subscription();
      this._timePickerOverlayRef.detach();
      this._timePickerOverlayRef.dispose();
      this._timePickerOverlayRef = null;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['showTime'] || changes['timeOnly']) {
      this._syncDateInputOptions();
      // Force the input to re-format with the new display format
      const value = this.dateControl.value;
      if (value != null) {
        this.dateControl.setValue(value, { emitEvent: false });
        this._cdr.markForCheck();
      }
    }
  }

  onBlur(): void {
    this.onTouched();
  }

  private convertValue(value: Date | null): DateReturnType {
    if (value === null) {
      return null;
    }

    switch (this.returnType) {
      case 'number':
        return value.getTime();
      case 'string':
        return value.toISOString();
      case 'date':
      default:
        return value;
    }
  }
}
